import { useEffect, useState } from 'react'
import { effectiveWorkPlan, firstOfNextMonth, localDayKey, type WorkPlanDayInput } from '@wf/shared'
import { ApiError } from '../api/client'
import { hhmmToMin, minToHHMM } from './timeMath'
import { useSetWorkPlan, useWorkPlan } from './useZeiten'

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const

interface DayDraft {
  start: string
  end: string
  breakMin: string
}

const EMPTY_DAY: DayDraft = { start: '', end: '', breakMin: '' }

/**
 * Dienstplan-Editor (W9.5): Wochenmuster Mo–So mit von/bis + Pause (Minuten). Leerer Tag = kein Solltag
 * (PUT löscht ihn). Unplausible Zeiten (Ende ≤ Beginn, Pause ≥ Spanne) weist die UI ab — der Server
 * verwirft sie zusätzlich still (deckungsgleiche Prüfung, Handoff W9.5). Sonntag ist planbar (§10 ArbZG
 * Kfz-Notdienst); ist er befüllt, blendet die UI einen ArbZG-Hinweis ein (§9 grundsätzliches Verbot).
 */
export function WorkplanEditor({ userId, userName }: { userId: number; userName: string }) {
  const plan = useWorkPlan(userId)
  const save = useSetWorkPlan()
  const [days, setDays] = useState<DayDraft[]>(Array.from({ length: 7 }, () => EMPTY_DAY))
  const [dirty, setDirty] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  // „Gültig ab" (Dienstplan-Versionierung 1b): '' = noch nicht initialisiert (Default kommt aus dem Server-Plan:
  // Ersteinrichtung = heute, sonst 1. des Folgemonats — der laufende Monat bleibt stabil).
  const [validFrom, setValidFrom] = useState('')
  const today = localDayKey(Date.now())

  // Server-Plan in die Entwurfsfelder spiegeln — aber NIE über ungespeicherte Eingaben (dirty): ein
  // Fokus-Refetch darf getippte Zeiten nicht kommentarlos zurücksetzen (Review-Fund). MA-Wechsel mountet
  // frisch (key={userId} im Aufrufer) → dirty startet dort wieder bei false. Gespiegelt wird die am GEWÄHLTEN
  // „gültig ab"-Tag wirksame Version (Versions-Auflösung @wf/shared) — so zeigt der Editor beim Bearbeiten
  // einer geplanten künftigen Version deren Werte statt des heutigen Musters.
  useEffect(() => {
    if (!plan.data || dirty) return
    const vf = validFrom || (plan.data.plan.length === 0 ? today : firstOfNextMonth(today))
    if (validFrom === '') setValidFrom(vf)
    const next = Array.from({ length: 7 }, () => ({ ...EMPTY_DAY }))
    for (const d of effectiveWorkPlan(plan.data.plan, vf)) {
      if (d.weekday < 0 || d.weekday > 6) continue
      next[d.weekday] = {
        start: minToHHMM(d.startMin),
        end: minToHHMM(d.endMin),
        breakMin: d.breakMin != null && d.breakMin > 0 ? String(d.breakMin) : '',
      }
    }
    setDays(next)
  }, [plan.data, dirty, validFrom, today])

  // Geplante künftige Versionen (validFrom > heute) — als Hinweis, damit nichts unbemerkt „scharf" liegt.
  const futureVersions = [...new Set((plan.data?.plan ?? []).map((p) => p.validFrom ?? '').filter((vf) => vf > today))].sort()

  function setDay(index: number, patch: Partial<DayDraft>) {
    setDirty(true)
    setDays((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }

  function submit() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validFrom)) {
      setLocalError('Bitte ein „gültig ab"-Datum wählen.')
      return
    }
    const payload: WorkPlanDayInput[] = []
    for (let weekday = 0; weekday < 7; weekday++) {
      const draft = days[weekday] as DayDraft
      const startMin = hhmmToMin(draft.start)
      const endMin = hhmmToMin(draft.end)
      if (startMin == null || endMin == null) {
        payload.push({ weekday, startMin: null, endMin: null }) // leerer Tag → kein Solltag in dieser Version
        continue
      }
      const breakMin = draft.breakMin.trim() === '' ? 0 : Math.max(0, Math.floor(Number(draft.breakMin)))
      if (endMin <= startMin || breakMin >= endMin - startMin) {
        setLocalError(`${WEEKDAYS[weekday]}: Ende muss nach Beginn liegen und die Pause kürzer als die Spanne sein`)
        return
      }
      payload.push({ weekday, startMin, endMin, breakMin })
    }
    setLocalError(null)
    save.mutate({ userId, days: payload, validFrom }, { onSuccess: () => setDirty(false) })
  }

  if (plan.isLoading) return <div className="muted">Dienstplan wird geladen…</div>
  if (plan.isError)
    return (
      <div className="error" role="alert">
        Dienstplan konnte nicht geladen werden.
      </div>
    )

  const serverError = save.error instanceof ApiError ? save.error.message : save.error ? 'Speichern fehlgeschlagen.' : null

  return (
    <div className="wf-card" aria-label={`Dienstplan ${userName}`}>
      <h3 className="wf-sectionhead">Dienstplan (Wochenmuster) · {userName}</h3>
      <p className="hint">Leerer Tag = kein Solltag. Das Soll fließt in Soll/Ist und die Überstunden-Rechnung ein.</p>
      <label className="wf-field">
        <span className="wf-label">Gültig ab (frühere Zeiträume bleiben unverändert)</span>
        <input
          className="wf-input"
          type="date"
          aria-label="Dienstplan gültig ab"
          value={validFrom}
          onChange={(e) => setValidFrom(e.target.value)}
        />
      </label>
      {validFrom !== '' && validFrom < today ? (
        <p className="hint" role="note">
          ⚠️ Rückwirkendes „gültig ab": verschiebt Soll/Überstunden ab diesem Datum neu. Bereits gespeicherte
          Versionen bleiben erhalten; die Änderung wird protokolliert.
        </p>
      ) : null}
      {futureVersions.length > 0 ? (
        <p className="hint" role="note">
          Geplante Änderung gültig ab {futureVersions.join(', ')} — zum Bearbeiten das „gültig ab" auf dieses Datum stellen.
        </p>
      ) : null}
      {days.map((draft, weekday) => (
        <div key={WEEKDAYS[weekday]} className="wf-planrow">
          <span className="wf-planrow__day">{WEEKDAYS[weekday]}</span>
          <input
            className="wf-input"
            type="time"
            aria-label={`${WEEKDAYS[weekday]} Beginn`}
            value={draft.start}
            onChange={(e) => setDay(weekday, { start: e.target.value })}
          />
          <span className="muted">–</span>
          <input
            className="wf-input"
            type="time"
            aria-label={`${WEEKDAYS[weekday]} Ende`}
            value={draft.end}
            onChange={(e) => setDay(weekday, { end: e.target.value })}
          />
          <input
            className="wf-input"
            type="number"
            min={0}
            step={5}
            placeholder="Pause (Min.)"
            aria-label={`${WEEKDAYS[weekday]} Pause in Minuten`}
            value={draft.breakMin}
            onChange={(e) => setDay(weekday, { breakMin: e.target.value })}
          />
        </div>
      ))}
      {days[6] && days[6].start.trim() !== '' && days[6].end.trim() !== '' ? (
        <p className="hint" role="note">
          ⚠️ Sonntagsarbeit ist grundsätzlich verboten (§ 9 ArbZG) und nur in Ausnahmen zulässig (z. B. Kfz-Notdienst,
          § 10). Für jeden Beschäftigungssonntag ist ein Ersatzruhetag binnen 2 Wochen zu gewähren (§ 11).
        </p>
      ) : null}
      {localError ? (
        <p className="error" role="alert">
          {localError}
        </p>
      ) : null}
      {serverError ? (
        <p className="error" role="alert">
          {serverError}
        </p>
      ) : null}
      <div className="wf-listfoot">
        <button type="button" className="wf-btn wf-btn--primary" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Dienstplan speichern'}
        </button>
        {save.isSuccess && !save.isPending ? <span className="hint">Gespeichert ✓</span> : null}
      </div>
    </div>
  )
}
